import { useState, useEffect, useMemo, useRef, type KeyboardEvent } from 'react';
import type { Server, Channel } from '../types';
import { api } from '../api';
import { Modal, Icon } from './ui';

interface CommandItem {
  id: string;
  type: 'channel' | 'action';
  serverName?: string;
  channelName?: string;
  channelType?: 'text' | 'voice' | 'dm';
  actionLabel?: string;
  actionIcon?: string;
  onPick: () => void;
}

interface Props {
  open: boolean;
  onClose: () => void;
  servers: Server[];
  onNavigate: (server: Server, channel: Channel) => void;
  onOpenSettings: () => void;
}

export function CommandPalette({ open, onClose, servers, onNavigate, onOpenSettings }: Props) {
  const [query, setQuery] = useState('');
  const [allChannels, setAllChannels] = useState<Map<string, Channel[]>>(new Map());
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load channels for all servers when palette opens
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    requestAnimationFrame(() => inputRef.current?.focus());

    // Fetch channels we don't have yet
    Promise.all(
      servers.map(async (s): Promise<[string, Channel[]]> => {
        if (allChannels.has(s.id)) return [s.id, allChannels.get(s.id)!];
        try {
          const channels = await api.listChannels(s.id);
          return [s.id, channels];
        } catch {
          return [s.id, []];
        }
      })
    ).then((entries) => {
      setAllChannels(new Map(entries));
    });
  }, [open, servers]);  // eslint-disable-line react-hooks/exhaustive-deps

  const items: CommandItem[] = useMemo(() => {
    const list: CommandItem[] = [];
    const q = query.trim().toLowerCase();

    // Channels from all servers
    for (const server of servers) {
      const channels = allChannels.get(server.id) || [];
      for (const channel of channels) {
        const haystack = `${server.name} ${channel.name}`.toLowerCase();
        if (q && !haystack.includes(q)) continue;
        list.push({
          id: `ch:${channel.id}`,
          type: 'channel',
          serverName: server.name,
          channelName: channel.name,
          channelType: channel.type,
          onPick: () => onNavigate(server, channel),
        });
      }
    }

    // Actions
    const actionMatches = (label: string) => !q || label.toLowerCase().includes(q);
    if (actionMatches('Settings')) {
      list.push({
        id: 'action:settings',
        type: 'action',
        actionLabel: 'Open Settings',
        actionIcon: 'settings',
        onPick: onOpenSettings,
      });
    }

    return list.slice(0, 50);
  }, [servers, allChannels, query, onNavigate, onOpenSettings]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % Math.max(1, items.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + Math.max(1, items.length)) % Math.max(1, items.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[activeIndex];
      if (item) {
        item.onPick();
        onClose();
      }
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Quick switcher" width="w-[520px]">
      <div className="space-y-3">
        <div className="bevel-down rounded-aim bg-elevated flex items-center gap-2 px-3 py-2">
          <Icon name="search" size={14} className="text-text-subtle" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search channels and servers…"
            className="flex-1 bg-transparent outline-hidden text-sm text-text"
          />
          <span className="text-[10px] text-text-subtle font-mono">Esc</span>
        </div>

        <div className="max-h-72 overflow-y-auto -mx-1">
          {items.length === 0 ? (
            <div className="text-center text-sm text-text-muted py-6">
              {allChannels.size === 0 ? 'Loading…' : 'No matches.'}
            </div>
          ) : (
            items.map((item, i) => (
              <div
                key={item.id}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => { item.onPick(); onClose(); }}
                className={`flex items-center gap-2.5 px-3 py-1.5 mx-1 rounded cursor-pointer ${
                  i === activeIndex ? 'bg-accent text-accent-text' : 'hover:bg-elevated'
                }`}
              >
                {item.type === 'channel' ? (
                  <>
                    <span
                      className={`shrink-0 ${i === activeIndex ? 'text-accent-text' : 'text-text-subtle'}`}
                      title={item.channelType}
                    >
                      {item.channelType === 'text' ? (
                        <span className="font-bold text-base leading-none">#</span>
                      ) : (
                        <Icon name="speaker" size={12} />
                      )}
                    </span>
                    <span className="text-sm font-semibold">{item.channelName}</span>
                    <span className={`text-xs ${i === activeIndex ? 'opacity-70' : 'text-text-subtle'}`}>
                      in {item.serverName}
                    </span>
                  </>
                ) : (
                  <>
                    <Icon name={item.actionIcon || 'settings'} size={14} />
                    <span className="text-sm font-semibold">{item.actionLabel}</span>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <div className="text-[10px] text-text-subtle text-center pt-1 border-t border-border-soft">
          ↑ ↓ to navigate · Enter to open · Esc to close
        </div>
      </div>
    </Modal>
  );
}
